import { Form, Input, Tabs, TabsProps } from "antd";
import { FormProvider } from "../../../../../../common/provider/form/Form.provider";
import { useState } from "react";
import { SimpleInput } from "../../../../../../common/components/simpleInput";
import style from "../style.module.scss";
import { handleNumericDecimalInput } from "../../../../../../common/utils/numericInputHandler";
import { CustomButton } from "../../../../../../common/components/CustomButton/CustomButton";
import { HttpStatusCode } from "axios";
import { HoldService } from "../core/HoldService";

interface HoldFormProps {
  hasBag: boolean;
  hasContainer: boolean;
  holds: any[];
  movements: "debarkation" | "embarkation" | "both";
  operationId: number;
  onFinish: () => void;
}

type Movement = "debarkation" | "embarkation";

export const HoldForm = ({
  hasBag,
  hasContainer,
  holds,
  movements,
  operationId,
  onFinish,
}: HoldFormProps) => {
  const [form] = Form.useForm();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<Movement>("debarkation");

  const holdsLength = holds?.length;

  const renderHoldsForm = (movement: Movement) => {
    const fieldName =
      movement === "debarkation"
        ? "debarkationHolds"
        : "embarkationHolds";

    return (
      <div className={style.form__movement}>
        {movements !== "both" && (
          <h3 className={style.container__title}>
          {movement === "debarkation"
            ? "Débarquement"
            : "Embarquement"}
        </h3>
        )}

        {holds?.map((hold, index) => (
          <div
            className={style.form__hold}
            key={`${movement}-${hold}`}
          >
            {/* Numéro de cale */}
            <div className={style.container__hold_number}>
              Cale n°{hold}
            </div>

            {/* Conteneurs */}
            {hasContainer ? (
              <>
                <div className={style.form__col}>
                  <SimpleInput
                    Form={Form}
                    rules={[
                      {
                        required: true,
                        message: "Champ requis.",
                      },
                    ]}
                    label="Nombre vide prévu"
                    name={[
                      fieldName,
                      index,
                      "plannedEmptyContainerCount",
                    ]}
                    customInput={<Input />}
                  />
                </div>

                <div className={style.form__col}>
                  <SimpleInput
                    Form={Form}
                    rules={[
                      {
                        required: true,
                        message: "Champ requis.",
                      },
                    ]}
                    label="Nombre plein prévu"
                    name={[
                      fieldName,
                      index,
                      "plannedFilledContainerCount",
                    ]}
                    customInput={<Input />}
                  />
                </div>
              </>
            ) : hasBag ? (
              <>
                <div className={style.form__col}>
                  <SimpleInput
                    Form={Form}
                    rules={[
                      {
                        required: true,
                        message: "Champ requis.",
                      },
                    ]}
                    label="Nombre prévu"
                    name={[fieldName, index, "plannedCount"]}
                    customInput={
                      <Input onKeyDown={handleNumericDecimalInput} />
                    }
                  />
                </div>

                <div className={style.form__col}>
                  <SimpleInput
                    Form={Form}
                    rules={[
                      {
                        required: true,
                        message: "Champ requis.",
                      },
                    ]}
                    label="Tonnage prévu"
                    name={[fieldName, index, "plannedTonnage"]}
                    customInput={<Input />}
                  />
                </div>
              </>
            ) : (
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  rules={[
                    {
                      required: true,
                      message: "Champ requis.",
                    },
                  ]}
                  label="Nombre prévu"
                  name={[fieldName, index, "plannedCount"]}
                  customInput={
                    <Input onKeyDown={handleNumericDecimalInput} />
                  }
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const buildHoldDistributions = (values: any[] = []) => {
    return values.map((holdValue, index) => {
      const distribution: any = {
        hold: holds[index],
      };

      if (hasContainer) {
        distribution.plannedEmptyContainerCount = Number(
          holdValue.plannedEmptyContainerCount
        );

        distribution.plannedFilledContainerCount = Number(
          holdValue.plannedFilledContainerCount
        );
      } else {
        distribution.plannedCount = Number(
          holdValue.plannedCount
        );

        if (hasBag) {
          distribution.plannedTonnage = holdValue.plannedTonnage;
        }
      }

      return distribution;
    });
  };

  const onSubmitHandler = async (
    values: any,
    movement: Movement
  ) => {
    try {
      setIsLoadingSubmit(true);

      const holdsTab = Array.from(
        { length: holdsLength },
        (_, i) => i + 1
      );

      const dataToPost: any = {
        id: operationId,
        holds: holdsTab,
      };
      
      if (movement === "debarkation") {
        dataToPost.debarkationMovement = {
          holdDistributions: buildHoldDistributions(
            values.debarkationHolds ?? []
          ),
        };
      }
      
      if (movement === "embarkation") {
        dataToPost.embarkationMovement = {
          holdDistributions: buildHoldDistributions(
            values.embarkationHolds ?? []
          ),
        };
      };

      const res = await HoldService.patchHolds(
        "hold-distribution",
        dataToPost
      );

      if (
        res?.status === HttpStatusCode.Created ||
        res?.status === HttpStatusCode.Ok
      ) {
        onFinish();
        form.resetFields();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const validateMovement = async (movement: Movement) => {
    const fieldName =
      movement === "debarkation"
        ? "debarkationHolds"
        : "embarkationHolds";
  
    try {
      await form.validateFields([fieldName], {
        recursive: true,
      });
  
      const values = form.getFieldsValue();
  
      await onSubmitHandler(values, movement);
    } catch (error) {
      // nothing
    }
  };

  const renderMovementTab = (movement: Movement) => (
    <div className={style.form}>
      {renderHoldsForm(movement)}

      <div className={style.form__action_wrapper}>
        <CustomButton
          type="button"
          className={style.form__action_button}
          isLoading={isLoadingSubmit}
          onClick={() => validateMovement(movement)}
        >
          Enregistrer
        </CustomButton>
      </div>
    </div>
  );

  const items: TabsProps["items"] = [
    {
      key: "debarkation",
      label: "Débarquement",
      children: renderMovementTab("debarkation"),
    },
    {
      key: "embarkation",
      label: "Embarquement",
      children: renderMovementTab("embarkation"),
    },
  ];

  return (
    <FormProvider
      Form={Form}
      form={form}
      debug={true}
      onFinish={() => {}}
      onFinishFailed={(err) => {
        console.log("err", err);
      }}
    >
      {movements === "both" ? (
        <Tabs
          activeKey={currentTab}
          items={items}
          onChange={(key) => setCurrentTab(key as Movement)}
        />
      ) : (
        <div className={style.form}>
          {renderHoldsForm(movements)}

          <div className={style.form__action_wrapper}>
          <CustomButton
            type="button"
            className={style.form__action_button}
            isLoading={isLoadingSubmit}
            onClick={() => validateMovement(movements)}
          >
            Enregistrer
          </CustomButton>
          </div>
        </div>
      )}
    </FormProvider>
  );
};